import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signOut as fbSignOut,
  User as FirebaseUser,
  signInWithEmailAndPassword,
  getIdTokenResult,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDocFromServer,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  FieldValue
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import firebaseConfig from '../../firebase-applet-config.json';

// Initialize Firebase App
export const app = initializeApp(firebaseConfig);

// Initialize Firestore with specific database ID from config
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);

// Initialize Authentication (Zero-Google Auth Architecture: Mobile OTP / Email + Password)
export const auth = getAuth(app);

// Initialize Firebase Storage for secure verification & media storage
export const storage = getStorage(app);

// Connection verification test required by Firebase integration skill
async function testConnection() {
  try {
    await getDocFromServer(doc(db, 'test', 'connection'));
  } catch (error) {
    if (error instanceof Error && error.message.includes('the client is offline')) {
      console.warn('Firebase client is offline or initializing. Please check configuration.');
    }
  }
}
testConnection();

// Standard OperationType and Error handling required by Firebase integration skill
export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const currentUser = auth.currentUser;
  const errorInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    operationType,
    path,
    authInfo: {
      userId: currentUser?.uid ?? null,
      email: currentUser?.email ?? null,
      emailVerified: currentUser?.emailVerified ?? null,
      isAnonymous: currentUser?.isAnonymous ?? null,
      tenantId: currentUser?.tenantId ?? null,
      providerInfo: currentUser?.providerData?.map((p) => ({
        providerId: p.providerId,
        email: p.email,
      })),
    },
  };
  console.error('Firestore Error: ', JSON.stringify(errorInfo));
  throw new Error(JSON.stringify(errorInfo));
}

// ================= AUTHENTICATION HELPERS (ZERO-GOOGLE AUTH ARCHITECTURE) =================

/**
 * Strips all undefined fields recursively so Firestore setDoc never encounters
 * "Unsupported field value: undefined"
 * Safely preserves Firestore FieldValues (like serverTimestamp()) and Dates.
 */
export function removeUndefinedDeep<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedDeep) as unknown as T;
  }
  if (typeof obj === 'object') {
    // Preserve Date and Firestore FieldValue sentinels (e.g. serverTimestamp())
    if (obj instanceof Date) return obj;
    if (obj instanceof FieldValue) return obj;
    if (
      obj.constructor &&
      typeof obj.constructor.name === 'string' &&
      obj.constructor.name.includes('FieldValue')
    ) {
      return obj;
    }
    if (typeof (obj as any)._methodName === 'string') {
      return obj;
    }

    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefinedDeep(value);
      }
    }
    return cleaned;
  }
  return obj;
}

/**
 * Google Sign-In is completely prohibited on PairUpToday.
 * All Client and Companion accounts must authenticate via Mobile OTP or Email + Password.
 */
export async function signInWithGoogle(
  _accountType: 'client' | 'companion' = 'client'
): Promise<never> {
  const err = new Error(
    'Google Sign-In is completely disabled on PairUpToday. Please sign in with your registered Mobile Number + SMS OTP or Email + Password.'
  );
  (err as any).code = 'auth/google-login-disabled';
  throw err;
}

export async function signOutUser(): Promise<void> {
  try {
    await fbSignOut(auth);
  } catch (err) {
    console.error('Sign-Out failed:', err);
    throw err;
  }
}

// ================= USER PROFILE SYNC =================
export interface FirestoreUserProfile {
  uid: string;
  name: string;
  displayName?: string;
  email: string;
  phone?: string;
  dob?: string;
  age?: number;
  gender?: string;
  city?: string;
  accountType: 'client' | 'companion';
  isVerified?: boolean;
  avatarUrl?: string;
  hourlyRate?: number;
  bio?: string;
  companionProfile?: any;
  profileStatus?: string;
  lifecycleStatus?: string;
  companionStatus?: string;
  companion_profile_status?: 'Active' | 'Inactive';
  isCompletedRegistration?: boolean;
  registeredAt?: string;
  membership_status?: 'INACTIVE' | 'PENDING' | 'ACTIVE' | 'CANCELLED' | 'Pending' | 'Active' | 'Expired' | 'Cancelled';
  payment_status?: 'Pending' | 'Success' | 'Failed' | 'Refunded';
  payment_verified?: boolean;
  membershipPlan?: string;
  membershipStatus?: string;
  nextBillingDate?: string;
  createdAt?: string;
  updatedAt?: any;
  services?: any[];
  wallet?: {
    availableBalance: number;
    totalEarned: number;
    pendingEscrow: number;
    completedMeetups: number;
    transactions: any[];
  };
}

const SERVER_MANAGED_USER_FIELDS = [
  'role',
  'isVerified',
  'isIdVerified',
  'kycStatus',
  'verificationStatus',
  'kycReviewedAt',
  'kycReviewedBy',
  'payment_verified',
  'payment_status',
  'companion_profile_status',
  'membership_status',
  'membership_id',
  'membership_expires_at',
  'membershipExpiry',
  'wallet',
  'accountStatus',
] as const;

export async function syncUserProfileToFirestore(profile: Partial<FirestoreUserProfile> & { uid?: string }): Promise<void> {
  const currentAuthUser = auth.currentUser;
  if (!currentAuthUser) {
    // When no active Firebase Auth session is present, direct client-side Firestore writes to users/{userId}
    // are rejected by security rules (which strictly require isOwner: request.auth.uid == userId).
    // Skipping prevents "Missing or insufficient permissions" errors.
    return;
  }

  // Enforce identity integrity: normal users only update their own Firebase Auth UID document
  const targetUid = currentAuthUser.uid;
  const path = `users/${targetUid}`;
  try {
    const userDocRef = doc(db, 'users', targetUid);
    const clientMutableProfile: Record<string, any> = { ...profile };
    for (const privilegedField of SERVER_MANAGED_USER_FIELDS) {
      delete clientMutableProfile[privilegedField];
    }
    const sanitizedData = removeUndefinedDeep({
      ...clientMutableProfile,
      uid: targetUid,
      updatedAt: serverTimestamp(),
    });
    await setDoc(userDocRef, sanitizedData, { merge: true });

    // When a companion profile is updated in Firestore, synchronize the lifecycle status
    if (profile.accountType === 'companion' || profile.companionProfile) {
      try {
        const { syncCompanionLifecycleInFirestore } = await import('../utils/companionStatusManager');
        await syncCompanionLifecycleInFirestore(targetUid, {
          userId: targetUid,
          context: {
            isProfileInfoComplete: Boolean(
              profile.displayName || profile.name || (profile.companionProfile as any)?.displayName
            ),
          },
        });
      } catch (lifecycleErr) {
        // Non-blocking sync notice
        console.warn('Companion lifecycle sync notice:', lifecycleErr);
      }
    }
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, path);
  }
}

export async function getUserProfileFromFirestore(uid: string): Promise<FirestoreUserProfile | null> {
  if (!uid) return null;
  const path = `users/${uid}`;
  try {
    const userDocRef = doc(db, 'users', uid);
    const snap = await getDoc(userDocRef);
    if (snap.exists()) {
      return snap.data() as FirestoreUserProfile;
    }
    return null;
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, path);
    return null;
  }
}

// Real-time listener for User Profile
export function subscribeToUserProfile(
  uid: string,
  onUpdate: (profile: FirestoreUserProfile | null) => void
): () => void {
  // Only attach onSnapshot listener if auth is ready and authenticated (Firebase Integration Skill constraint)
  if (!uid || !auth.currentUser) {
    return () => {};
  }
  const targetUid = auth.currentUser.uid;
  const path = `users/${targetUid}`;
  const userDocRef = doc(db, 'users', targetUid);
  return onSnapshot(
    userDocRef,
    (snapshot) => {
      if (snapshot.exists()) {
        onUpdate(snapshot.data() as FirestoreUserProfile);
      } else {
        onUpdate(null);
      }
    },
    (error) => {
      handleFirestoreError(error, OperationType.GET, path);
    }
  );
}

export function isUserAccountCompleted(data: any): boolean {
  if (!data) return false;
  // If explicitly flagged as incomplete
  if (data.isCompletedRegistration === false || data.isCompleted === false) {
    return false;
  }
  // If payment status is Failed, Cancelled, or Pending
  if (data.payment_status === 'Failed' || data.payment_status === 'Cancelled') {
    return false;
  }
  // For companions: must have active profile and completed/verified payment
  if (data.accountType === 'companion') {
    if (data.companion_profile_status === 'Inactive' || data.companionStatus === 'MEMBERSHIP_PENDING') {
      return false;
    }
    if (data.membership_status === 'PENDING' || data.membership_status === 'Pending' || data.payment_status === 'Pending') {
      return false;
    }
    if (data.payment_verified === false) {
      return false;
    }
    return data.isCompletedRegistration === true || data.isCompleted === true;
  }
  // For clients or standard accounts: must have completed registration
  return data.isCompletedRegistration === true || data.isCompleted === true;
}

export async function checkEmailExistsInFirestore(email: string): Promise<boolean> {
  try {
    const normalized = email.trim().toLowerCase();
    if (!normalized || !normalized.includes('@')) return false;
    const q = query(collection(db, 'users'), where('email', '==', normalized));
    const snap = await getDocs(q);
    if (snap.empty) return false;
    let isCompleted = false;
    snap.forEach((docSnap) => {
      const data = docSnap.data();
      if (isUserAccountCompleted(data)) {
        isCompleted = true;
      }
    });
    return isCompleted;
  } catch (error) {
    console.warn('Firestore email existence check notice:', error);
    return false;
  }
}

export async function checkPhoneExistsInFirestore(phone: string): Promise<boolean> {
  try {
    const normalized = phone.trim().replace(/\D/g, '');
    if (!normalized || normalized.length < 10) return false;
    const last10 = normalized.slice(-10);

    // 1. Check with exact normalized phone
    const q = query(collection(db, 'users'), where('phone', '==', normalized));
    const snap = await getDocs(q);
    let isCompleted = false;
    snap.forEach((docSnap) => {
      if (isUserAccountCompleted(docSnap.data())) {
        isCompleted = true;
      }
    });
    if (isCompleted) return true;

    // 2. If phone had country code prefix, also check last 10 digits
    if (last10 !== normalized) {
      const q2 = query(collection(db, 'users'), where('phone', '==', last10));
      const snap2 = await getDocs(q2);
      snap2.forEach((docSnap) => {
        if (isUserAccountCompleted(docSnap.data())) {
          isCompleted = true;
        }
      });
    }
    return isCompleted;
  } catch (error) {
    console.warn('Firestore phone existence check notice:', error);
    return false;
  }
}

// ================= BOOKINGS SYNC =================
export interface FirestoreBooking {
  id: string;
  clientId: string;
  companionId: string;
  companionName: string;
  date: string;
  time: string;
  durationHours: number;
  occasion: string;
  meetingLocation: string;
  city: string;
  totalAmount: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  meetOtp?: string;
  createdAt?: string;
}

export async function saveBookingToFirestore(bookingData: FirestoreBooking): Promise<boolean> {
  const path = `bookings/${bookingData.id}`;
  try {
    const bookingDocRef = doc(db, 'bookings', bookingData.id);
    await setDoc(bookingDocRef, {
      ...bookingData,
      createdAt: bookingData.createdAt || new Date().toISOString(),
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    handleFirestoreError(error, OperationType.CREATE, path);
    return false;
  }
}

export async function updateBookingStatusInFirestore(
  bookingId: string,
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled'
): Promise<boolean> {
  const currentAuthUser = auth.currentUser;
  if (!currentAuthUser) {
    // When no active Firebase Auth session is present, client-side writes will be rejected by security rules.
    return false;
  }
  const path = `bookings/${bookingId}`;
  try {
    const bookingDocRef = doc(db, 'bookings', bookingId);
    await updateDoc(bookingDocRef, {
      status,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    handleFirestoreError(error, OperationType.UPDATE, path);
    return false;
  }
}

export async function getUserBookingsFromFirestore(userId: string): Promise<FirestoreBooking[]> {
  if (!userId) return [];
  const path = 'bookings';
  try {
    const bookingsCol = collection(db, 'bookings');
    const q = query(bookingsCol, where('clientId', '==', userId));
    const snap = await getDocs(q);
    const bookings: FirestoreBooking[] = [];
    snap.forEach((docSnap) => {
      bookings.push(docSnap.data() as FirestoreBooking);
    });
    return bookings;
  } catch (error) {
    handleFirestoreError(error, OperationType.LIST, path);
    return [];
  }
}

// Real-time listener for Bookings
export function subscribeToUserBookings(
  userId: string,
  onUpdate: (bookings: FirestoreBooking[]) => void,
  role: 'client' | 'companion' = 'client'
): () => void {
  if (!userId) return () => {};
  const path = 'bookings';
  const bookingsCol = collection(db, 'bookings');
  const fieldToMatch = role === 'companion' ? 'companionId' : 'clientId';
  const q = query(bookingsCol, where(fieldToMatch, '==', userId));

  return onSnapshot(
    q,
    (snapshot) => {
      const bookings: FirestoreBooking[] = [];
      snapshot.forEach((docSnap) => {
        bookings.push(docSnap.data() as FirestoreBooking);
      });
      onUpdate(bookings);
    },
    (error) => {
      handleFirestoreError(error, OperationType.LIST, path);
    }
  );
}

// ================= WISHLIST SYNC =================
export async function saveWishlistItemToFirestore(userId: string, companionId: string): Promise<void> {
  const currentAuthUser = auth.currentUser;
  if (!currentAuthUser) return;
  const targetUserId = currentAuthUser.uid;
  const path = `users/${targetUserId}/wishlist/${companionId}`;
  try {
    const itemRef = doc(db, 'users', targetUserId, 'wishlist', companionId);
    await setDoc(itemRef, {
      companionId,
      addedAt: new Date().toISOString(),
    });
  } catch (error) {
    handleFirestoreError(error, OperationType.CREATE, path);
  }
}

export async function removeWishlistItemFromFirestore(userId: string, companionId: string): Promise<void> {
  const currentAuthUser = auth.currentUser;
  if (!currentAuthUser) return;
  const targetUserId = currentAuthUser.uid;
  const path = `users/${targetUserId}/wishlist/${companionId}`;
  try {
    const itemRef = doc(db, 'users', targetUserId, 'wishlist', companionId);
    await deleteDoc(itemRef);
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, path);
  }
}

export async function getWishlistFromFirestore(userId: string): Promise<string[]> {
  const currentAuthUser = auth.currentUser;
  if (!currentAuthUser) return [];
  const targetUserId = currentAuthUser.uid;
  const path = `users/${targetUserId}/wishlist`;
  try {
    const wishlistCol = collection(db, 'users', targetUserId, 'wishlist');
    const snap = await getDocs(wishlistCol);
    const list: string[] = [];
    snap.forEach((docSnap) => {
      list.push(docSnap.id);
    });
    return list;
  } catch (error) {
    handleFirestoreError(error, OperationType.LIST, path);
    return [];
  }
}

// Real-time listener for Wishlist
export function subscribeToWishlist(
  userId: string,
  onUpdate: (wishlist: string[]) => void
): () => void {
  const currentAuthUser = auth.currentUser;
  if (!currentAuthUser) return () => {};
  const targetUserId = currentAuthUser.uid;
  const path = `users/${targetUserId}/wishlist`;
  const wishlistCol = collection(db, 'users', targetUserId, 'wishlist');

  return onSnapshot(
    wishlistCol,
    (snapshot) => {
      const list: string[] = [];
      snapshot.forEach((docSnap) => {
        list.push(docSnap.id);
      });
      onUpdate(list);
    },
    (error) => {
      handleFirestoreError(error, OperationType.LIST, path);
    }
  );
}

// ================= PAYMENTS REAL-TIME SYNC =================
export interface FirestorePaymentRecord {
  uid: string;
  membership_id?: string;
  razorpay_order_id: string;
  razorpay_payment_id?: string;
  amount: number;
  currency: string;
  status: string;
  payment_verified?: boolean;
  webhook_verified?: boolean;
  payment_method?: string;
  transaction_time?: string;
  gateway?: string;
  failure_reason?: string;
  plan_id?: string;
  customer_email?: string;
  customer_phone?: string;
}

export async function savePaymentToFirestore(paymentData: FirestorePaymentRecord): Promise<boolean> {
  void paymentData;
  throw new Error('Payment records are server-managed. Use the verified Razorpay backend workflow.');
}

export function subscribeToPayments(
  uid: string,
  onUpdate: (payments: FirestorePaymentRecord[]) => void
): () => void {
  const path = 'payments';
  const paymentsQuery = query(collection(db, 'payments'), where('uid', '==', uid));

  return onSnapshot(
    paymentsQuery,
    (snapshot) => {
      const payments: FirestorePaymentRecord[] = [];
      snapshot.forEach((docSnap) => {
        payments.push(docSnap.data() as FirestorePaymentRecord);
      });
      onUpdate(payments);
    },
    (error) => {
      handleFirestoreError(error, OperationType.LIST, path);
    }
  );
}

/**
 * Validates whether the currently signed-in Firebase user has verified Administrator privileges.
 * Performs dual-check:
 * 1. Server-assigned custom claims (claims.admin === true || claims.role === 'admin' || claims.role === 'SUPER_ADMIN')
 * 2. Server-managed /admins/{uid} Firestore document check
 */
const ALLOWED_ADMIN_ROLES = new Set([
  'SUPER_ADMIN',
  'ADMIN',
  'KYC_ADMIN',
  'FINANCE_ADMIN',
  'SUPPORT_ADMIN',
  'AUDITOR',
]);

export async function checkCurrentUserAdminStatus(user?: FirebaseUser | null): Promise<{
  isAdmin: boolean;
  hasPrimaryAdminRole?: boolean;
  mfaVerified?: boolean;
  mfaRequired?: boolean;
  phone?: string;
  role: string | null;
  email: string | null;
  source: 'custom_claim' | 'firestore_doc' | 'none';
}> {
  const targetUser = user || auth.currentUser;
  if (!targetUser) {
    return { isAdmin: false, role: null, email: null, source: 'none' };
  }

  try {
    // Require server-assigned custom claims (claims.admin === true)
    const tokenResult = await getIdTokenResult(targetUser, true);
    const claims = tokenResult.claims;
    if (claims.admin !== true) {
      return { isAdmin: false, role: null, email: targetUser.email || null, source: 'none' };
    }

    // 1. Try client-side Firestore /admins/{uid} read first (named DB, then (default) DB fallback)
    try {
      let adminDocSnap;
      try {
        adminDocSnap = await getDoc(doc(db, 'admins', targetUser.uid));
      } catch {
        const defaultDb = getFirestore(app);
        adminDocSnap = await getDoc(doc(defaultDb, 'admins', targetUser.uid));
      }
      if (adminDocSnap.exists() && adminDocSnap.data()?.status === 'ACTIVE') {
        const adminData = adminDocSnap.data() || {};
        const docRole = String(adminData.role || '');
        const claimRole = claims.role ? String(claims.role) : '';
        if (ALLOWED_ADMIN_ROLES.has(docRole) && (!claimRole || claimRole === docRole)) {
          const hasFirebaseSecondFactor = Boolean((claims as any).firebase?.sign_in_second_factor);
          const hasClaimMfa = claims.mfaVerified === true;
          const isPasswordAuth =
            claims.authMethod === 'PASSWORD' ||
            adminData.lastLoginMethod === 'PASSWORD';
          const mfaVerifiedUntilMs = Number(adminData.mfaVerifiedUntilMs || 0);
          const hasServerMfaSession = Number.isFinite(mfaVerifiedUntilMs) && mfaVerifiedUntilMs > Date.now();
          const mfaVerified = hasFirebaseSecondFactor || hasClaimMfa || hasServerMfaSession;
          const adminPhone = String(adminData.phone || adminData.phoneNumber || targetUser.phoneNumber || '').trim();
          const mfaMandated = adminData.mfaRequired === true || adminData.mfa_enforced === true;

          if (mfaVerified || (isPasswordAuth && !mfaMandated)) {
            return {
              isAdmin: true,
              hasPrimaryAdminRole: true,
              mfaVerified,
              mfaRequired: false,
              phone: adminPhone,
              role: docRole,
              email: targetUser.email || null,
              source: 'custom_claim',
            };
          }

          // Before concluding MFA is unverified, also check server-side /api/admin/auth/status
          // in case the MFA session was verified in the backend registry or PostgreSQL.
          try {
            const { apiFetch } = await import('./apiConfig');
            const srvRes = await apiFetch('/api/admin/auth/status', {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${tokenResult.token}`,
                'x-admin-token': tokenResult.token,
              },
            });
            if (srvRes.ok) {
              const srvData = await srvRes.json();
              if (srvData?.success && srvData?.hasPrimaryAdminRole) {
                return {
                  isAdmin: Boolean(srvData.isAdmin),
                  hasPrimaryAdminRole: true,
                  mfaVerified: Boolean(srvData.mfaVerified),
                  mfaRequired: Boolean(srvData.mfaRequired),
                  phone: String(srvData.phone || adminPhone || '').trim(),
                  role: String(srvData.role || docRole),
                  email: targetUser.email || null,
                  source: 'custom_claim',
                };
              }
            }
          } catch {
            // Fall back to Firestore result below
          }

          return {
            isAdmin: false,
            hasPrimaryAdminRole: true,
            mfaVerified: false,
            mfaRequired: true,
            phone: adminPhone,
            role: docRole,
            email: targetUser.email || null,
            source: 'custom_claim',
          };
        }
      }
    } catch {
      // Firestore read failed (e.g. free-tier quota exhausted); fall back to authoritative server status endpoint
    }

    // 2. Server-side authoritative fallback (/api/admin/auth/status backed by Firestore + PostgreSQL admin_users)
    try {
      const { apiFetch } = await import('./apiConfig');
      const srvRes = await apiFetch('/api/admin/auth/status', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${tokenResult.token}`,
          'x-admin-token': tokenResult.token,
        },
      });
      if (srvRes.ok) {
        const srvData = await srvRes.json();
        if (srvData?.success && srvData?.hasPrimaryAdminRole && ALLOWED_ADMIN_ROLES.has(String(srvData.role || ''))) {
          return {
            isAdmin: Boolean(srvData.isAdmin),
            hasPrimaryAdminRole: true,
            mfaVerified: Boolean(srvData.mfaVerified),
            mfaRequired: Boolean(srvData.mfaRequired),
            phone: String(srvData.phone || targetUser.phoneNumber || '').trim(),
            role: String(srvData.role),
            email: targetUser.email || null,
            source: 'custom_claim',
          };
        }
      }
    } catch {
      // Ignore network error
    }

    return { isAdmin: false, role: null, email: targetUser.email || null, source: 'none' };
  } catch (err) {
    console.warn('Error checking current user admin status:', err);
    return { isAdmin: false, role: null, email: targetUser.email || null, source: 'none' };
  }
}

/**
 * Send a Firebase password-reset link to an administrator email address.
 */
export async function sendAdminPasswordReset(email: string): Promise<void> {
  const cleanEmail = String(email || '').trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes('@')) {
    throw new Error('Please enter a valid administrator email address.');
  }
  await sendPasswordResetEmail(auth, cleanEmail);
}

/**
 * Sign in administrator using Firebase Authentication with Email and Password
 */
export async function adminSignInWithFirebase(email: string, pass: string): Promise<{
  user: FirebaseUser;
  isAdmin: boolean;
  hasPrimaryAdminRole?: boolean;
  mfaVerified?: boolean;
  mfaRequired?: boolean;
  phone?: string;
  role: string | null;
  idToken: string;
}> {
  const userCredential = await signInWithEmailAndPassword(auth, email, pass);

  if (!userCredential.user.emailVerified) {
    try {
      await sendEmailVerification(userCredential.user);
    } catch (verificationError: any) {
      await fbSignOut(auth).catch(() => undefined);

      const code = verificationError?.code || "unknown";
      throw new Error(
        `Verification email failed (${code}). Please check Firebase Authentication settings.`
      );
    }

    await fbSignOut(auth);
    throw new Error(
      "Verification email sent. Check your Inbox and Spam, click the verification link, then sign in again."
    );
  }

  const status = await checkCurrentUserAdminStatus(userCredential.user);
  const idToken = await userCredential.user.getIdToken(true);

  return {
    user: userCredential.user,
    isAdmin: status.isAdmin,
    hasPrimaryAdminRole: status.hasPrimaryAdminRole,
    mfaVerified: status.mfaVerified,
    mfaRequired: status.mfaRequired,
    phone: status.phone,
    role: status.role,
    idToken,
  };
}

// Send a genuine Firebase email verification link to the signed-in user.
export async function sendCurrentUserVerificationEmail(): Promise<void> {
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Please sign in before requesting email verification.');
  }

  if (user.emailVerified) {
    throw new Error('This email address is already verified.');
  }

  await sendEmailVerification(user);
}
